import React from 'react';
import { Pie as ChartJSPie } from 'react-chartjs-2';
import tinycolor from 'tinycolor2';
import type { ChartConfig } from '../../../services/analytics-service';

interface AnalyticsPieChartProps {
  chartConfig: ChartConfig;
  onButtonClick?: (buttonId: string) => Promise<void>;
  activeButtonId: string;
  isLoading?: boolean;
  ChartWrapper: React.ComponentType<{
    chartConfig: ChartConfig;
    chartContent: React.ReactNode;
    onButtonClick?: (buttonId: string) => Promise<void>;
    activeButtonId: string;
    isLoading?: boolean;
  }>;
}

export const AnalyticsPieChart: React.FC<AnalyticsPieChartProps> = ({
  chartConfig,
  onButtonClick,
  activeButtonId,
  isLoading,
  ChartWrapper,
}) => {
  const chartData = chartConfig.data || [];
  const baseColor = chartConfig.colors || "#72BFE6";

  const generateShades = (base: string, count: number): string[] => {
    const shades = [];
    for (let i = 0; i < count; i++) {
      const ratio = (i / count) * 0.45;
      const shade = tinycolor(base).lighten(ratio * 100).toHexString();
      shades.push(shade);
    }
    return shades.reverse();
  };

  const colors = generateShades(baseColor[0], chartData.length);
  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  const data = {
    labels: chartData.map(item => item.name),
    datasets: [
      {
        data: chartData.map(item => item.value),
        backgroundColor: colors,
        borderWidth: 4,
        borderRadius: 6,
        borderColor: "#fff",
        cutout: "25%",
        spacing: 1,
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const value = context.parsed ?? 0;
            const percentage = ((value / total) * 100).toFixed(1);
            return `${value.toLocaleString()} (${percentage}%)`;
          }
        }
      }
    }
  };

  const chartContent = (
    <div className="flex items-center gap-12">
      <div className="w-[50%] h-[400px] flex items-center justify-center">
        <ChartJSPie data={data} options={options} />
      </div>

      <div className="flex flex-col gap-3 w-[55%]">
        {chartData.map((item, index) => {
          const color = colors[index];
          return (
            <div key={index} className="flex items-start gap-3">
              <div
                className="w-1 rounded-full mt-1"
                style={{ height: "32px", backgroundColor: color }}
              />

              <div className="flex flex-col">
                <span className="text-gray-600 text-sm font-medium">{item.name}</span>

                <span className="text-gray-900 text-lg font-semibold leading-tight">
                  {item.value.toLocaleString()}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <ChartWrapper
      chartConfig={chartConfig}
      chartContent={chartContent}
      onButtonClick={onButtonClick}
      activeButtonId={activeButtonId}
      isLoading={isLoading}
    />
  );
};
